/**
 * key 表单的纯逻辑：初值、校验、提交体。没有 Vue、没有 i18n、没有网络 —— 可单测。
 *
 * 校验规则与 `llm_key` 表上的 CHECK 约束一致（`packages/keyrouter/src/db/tables/llm_key.cjs`）：
 * `weight > 0`、`max_concurrency > 0`、`max_tpm` 为 NULL 或 > 0、`max_rpm` 同理。
 * 表单里数字都是字符串（原生 input 的值），空串表示「不限」→ null。
 *
 * **明文密钥不回显**：`keyFormFromRow` 永远把 `api_key` 置空。填了就是替换一把新密钥，
 * 不填就是不动密钥。所以 `api_key` 只在新建时必填。
 *
 * 校验返回的是**错误码**不是文案：这个模块不认识任何语言，文案由页面查 i18n。
 *
 * @typedef {'required'|'positiveInt'|'integer'|'tooLong'} KeyFieldError
 * @typedef {{ key_id: string|null, api_key: string, account_id: string, label: string, base_url: string,
 *   weight: string, max_concurrency: string, max_tpm: string, max_rpm: string,
 *   priority: string, enabled: boolean }} KeyForm
 */

const API_KEY_MAX_LENGTH = 256
const ACCOUNT_ID_MAX_LENGTH = 128
const LABEL_MAX_LENGTH = 64
const BASE_URL_MAX_LENGTH = 512

/** 新建一把 key 的初值。并发上限 4 与表上的 default 一致。 */
export function emptyKeyForm() {
  return {
    key_id: null,
    api_key: '',
    account_id: '',
    label: '',
    base_url: '',
    weight: '1',
    max_concurrency: '4',
    max_tpm: '',
    max_rpm: '',
    priority: '0',
    enabled: true,
  }
}

/**
 * 从接口返回的 key 行填表单（编辑）。`api_key` 空 —— 接口本来也只给脱敏值，无从回显。
 * @param {import('../../api/llm/index.js').KeyRow} row
 * @returns {KeyForm}
 */
export function keyFormFromRow(row) {
  return {
    key_id: row.id,
    api_key: '',
    account_id: row.account_id ?? '',
    label: row.label ?? '',
    base_url: row.base_url ?? '',
    weight: String(row.weight),
    max_concurrency: String(row.max_concurrency),
    max_tpm: row.max_tpm == null ? '' : String(row.max_tpm),
    max_rpm: row.max_rpm == null ? '' : String(row.max_rpm),
    priority: String(row.priority),
    enabled: row.enabled,
  }
}

/**
 * 逐字段校验。返回 `{ 字段: 错误码 }`；全部合法时返回空对象。
 * @param {KeyForm} form
 * @returns {Record<string, KeyFieldError>}
 */
export function validateKeyForm(form) {
  /** @type {Record<string, KeyFieldError>} */
  const errors = {}

  // 新建必须带明文密钥；编辑时留空 = 不换密钥。
  if (!form.key_id && form.api_key.trim() === '') errors.api_key = 'required'
  else if (form.api_key.length > API_KEY_MAX_LENGTH) errors.api_key = 'tooLong'

  // 复合凭据的账号侧（如豆包 appId）：可选，单一密钥厂商留空。
  if (form.account_id.length > ACCOUNT_ID_MAX_LENGTH) errors.account_id = 'tooLong'

  // label 必填：池子里 10 把 key 在界面上是 10 行看不出区别的星号，没有 label 认不出哪把是哪个账号。
  if (form.label.trim() === '') errors.label = 'required'
  else if (form.label.length > LABEL_MAX_LENGTH) errors.label = 'tooLong'

  if (form.base_url.length > BASE_URL_MAX_LENGTH) errors.base_url = 'tooLong'

  if (!isPositiveInt(form.weight)) errors.weight = 'positiveInt'
  if (!isPositiveInt(form.max_concurrency)) errors.max_concurrency = 'positiveInt'
  if (!isBlankOrPositiveInt(form.max_tpm)) errors.max_tpm = 'positiveInt'
  if (!isBlankOrPositiveInt(form.max_rpm)) errors.max_rpm = 'positiveInt'
  if (!isInt(form.priority)) errors.priority = 'integer'

  return errors
}

/**
 * 表单 → 提交体。**只在校验通过后调用**：遇到不合法的值直接抛错，不塞默认值继续跑。
 * @param {KeyForm} form
 */
export function toKeySaveBody(form) {
  const errors = validateKeyForm(form)
  const bad = Object.keys(errors)
  if (bad.length > 0) throw new Error(`toKeySaveBody: invalid key form fields: ${bad.join(', ')}`)
  return {
    key_id: form.key_id,
    api_key: form.api_key.trim() === '' ? null : form.api_key.trim(),
    account_id: form.account_id.trim() === '' ? null : form.account_id.trim(),
    label: form.label.trim(),
    base_url: form.base_url.trim() === '' ? null : form.base_url.trim(),
    weight: Number(form.weight),
    max_concurrency: Number(form.max_concurrency),
    max_tpm: blankToNull(form.max_tpm),
    max_rpm: blankToNull(form.max_rpm),
    priority: Number(form.priority),
    enabled: form.enabled,
  }
}

/**
 * 把某一行的现状原样提交、只翻转 enabled —— 行内「停用 / 启用」不该顺手改掉别的配置列。
 * @param {import('../../api/llm/index.js').KeyRow} row
 * @param {boolean} enabled
 */
export function toKeyEnabledBody(row, enabled) {
  return toKeySaveBody({ ...keyFormFromRow(row), enabled })
}

/** @param {string} value */
function isInt(value) {
  return /^-?\d+$/.test(value.trim())
}

/** @param {string} value */
function isPositiveInt(value) {
  return /^\d+$/.test(value.trim()) && Number(value) > 0
}

/** @param {string} value */
function isBlankOrPositiveInt(value) {
  return value.trim() === '' || isPositiveInt(value)
}

/** @param {string} value */
function blankToNull(value) {
  return value.trim() === '' ? null : Number(value)
}
